import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Chip,
    List,
    ListItemButton,
    ListItemText
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { api } from 'common';
import moment from 'moment/min/moment-with-locales';
import AlertDialog from '../components/AlertDialog';
import CircularLoading from '../components/CircularLoading';

const useStyles = makeStyles(() => ({
    container: {
        backgroundColor: 'var(--bg-main)',
        minHeight: '100vh',
        padding: '24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
    },
    card: {
        borderRadius: '20px',
        backgroundColor: 'var(--bg-card)',
        border: 'var(--card-border)',
        boxShadow: 'var(--shadow)',
        width: '100%',
        maxWidth: '460px',
        padding: '30px',
        marginTop: '40px'
    },
    logo: {
        color: '#E9AE31',
        fontWeight: 900,
        fontSize: '24px',
        letterSpacing: '2px',
        marginBottom: '4px',
        textAlign: 'center'
    },
    textField: {
        marginBottom: '18px',
        "& label.Mui-focused": { color: '#E9AE31' },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": { borderColor: '#E9AE31' },
            "& input": { color: 'var(--text-main)' }
        },
        "& .MuiInputLabel-root": { color: 'var(--text-secondary)' }
    },
    btn: {
        borderRadius: '19px',
        backgroundColor: '#E9AE31',
        minHeight: '48px',
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '16px',
        '&:hover': { backgroundColor: '#D19825' },
        '&.Mui-disabled': { backgroundColor: 'rgba(233, 174, 49, 0.35)', color: 'rgba(255,255,255,0.6)' }
    },
    linkBtn: {
        color: '#E9AE31',
        textTransform: 'none',
        marginTop: '8px'
    },
    statusRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
    },
    pickerItem: {
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        marginBottom: '10px'
    }
}));

// Public (unauthenticated) page reached from the renewal reminder email - the app-independent
// fallback next to the in-app renewal path (re-scanning the sticker, which already routes
// through the existing PaymentGateway screen). Only ever asks for an email, never a serial
// number - customers only ever see their QR code, not the plain serial printed under it, so a
// serial-first lookup isn't something a real visitor could actually complete. Flow: confirm
// the visitor owns that email via the same OTP mechanism used elsewhere in the app, look up
// every registration under it (mirrors findMyVehicles' phone-based multi-vehicle lookup), then
// reuse the exact same SkipCash checkout endpoint the mobile app's PaymentWebView drives -
// render_checkout already supports a plain top-level-browser GET redirect, so no new payment
// integration was needed here.
export default function Renew() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const settings = useSelector((state) => state.settingsdata.settings);

    const { requestEmailOtp, verifyEmailOtp, getRegistrationsByEmail, getHost } = api;

    const [step, setStep] = useState('lookup'); // lookup | otp | picker | status
    const [email, setEmail] = useState(searchParams.get('email') || '');
    // Serial only ever arrives as a link hint from the reminder email (to auto-select the
    // right registration when someone has more than one) - never shown or asked for.
    const hintSerial = searchParams.get('serial') || '';
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [registrations, setRegistrations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [alertState, setAlertState] = useState({ open: false, msg: '' });

    const handleAlertClose = (e) => {
        e.preventDefault();
        setAlertState({ open: false, msg: '' });
    };

    const handleSendCode = async () => {
        const trimmedEmail = email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setAlertState({ open: true, msg: t('renew_invalid_email') });
            return;
        }

        setLoading(true);
        try {
            const otpResult = await dispatch(requestEmailOtp(trimmedEmail));
            if (!otpResult || !otpResult.success) {
                throw new Error((otpResult && otpResult.error) || 'Failed to send verification code');
            }
            setEmail(trimmedEmail);
            setStep('otp');
        } catch (error) {
            setAlertState({ open: true, msg: error.message || 'Something went wrong' });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp.trim()) {
            setAlertState({ open: true, msg: t('renew_invalid_otp') });
            return;
        }
        setLoading(true);
        try {
            const result = await dispatch(verifyEmailOtp(email, otp.trim()));
            if (!result || !result.success) {
                throw new Error((result && result.error) || 'Verification failed');
            }

            const regResult = await dispatch(getRegistrationsByEmail(email));
            // Distinguish a real network/server failure from a genuine zero-result lookup -
            // getRegistrationsByEmail's catch block also resolves to {registrations: []} on
            // failure (e.g. a non-JSON response), which would otherwise silently look
            // identical to "this email really has no registrations".
            if (!regResult || regResult.error) {
                throw new Error((regResult && regResult.error) || 'Failed to look up registrations');
            }
            const found = regResult.registrations || [];
            if (found.length === 0) {
                setAlertState({ open: true, msg: t('renew_not_found') });
                return;
            }

            setRegistrations(found);
            if (found.length === 1) {
                setSelected(found[0]);
                setStep('status');
            } else {
                // If the reminder link's serial hint matches one of this email's
                // registrations, skip straight to it instead of making them pick.
                const hinted = hintSerial && found.find((r) => r.serialNumber === hintSerial);
                if (hinted) {
                    setSelected(hinted);
                    setStep('status');
                } else {
                    setStep('picker');
                }
            }
        } catch (error) {
            setAlertState({ open: true, msg: error.message || 'Verification failed' });
        } finally {
            setLoading(false);
        }
    };

    const handlePick = (reg) => {
        setSelected(reg);
        setStep('status');
    };

    const isExpired = selected && selected.expiryDate && selected.expiryDate < Date.now();
    const price = (settings && settings.qr_activation_price) || 20;

    const handleRenew = () => {
        if (settings && settings.AllowCriticalEditsAdmin === false) {
            setAlertState({ open: true, msg: t('demo_mode') });
            return;
        }
        // Same host every other action on this page already resolves to - NOT
        // window.location.origin, which is wrong on localhost during development (no
        // Firebase Hosting rewrite there to proxy /skipcash-link etc. through to the actual
        // Cloud Functions - the request would just get swallowed by this page's own SPA
        // routing instead).
        const base = getHost();
        const nameParts = String((selected && selected.name) || '').trim().split(/\s+/);
        const firstName = nameParts[0] || 'Customer';
        const lastName = nameParts.slice(1).join(' ') || 'Customer';

        // redirect_path overrides /success and /cancel's default post-payment destination
        // (historically /bookings, or /userwallet for wallet top-ups - neither applies to a
        // renewal payment, which is what sent the user to /bookings before this fix). Lands
        // on the homepage instead of back on /renew itself, since that would mean asking for
        // another OTP just to see a confirmation.
        const returnUrl = `${base}/skipcash-process?order_id=${selected.registrationId}&amount=${price}&web_url=${encodeURIComponent(base)}&redirect_path=${encodeURIComponent('/')}`;
        const webhookUrl = `${base}/skipcash-webhook`;
        const params = new URLSearchParams({
            order_id: selected.registrationId,
            amount: String(price),
            currency: 'QAR',
            return_url: returnUrl,
            webhook_url: webhookUrl,
            web_url: base,
            product_name: 'QR Activation Renewal',
            quantity: '1',
            mobile_no: selected.phone || '',
            email,
            first_name: firstName,
            last_name: lastName
        });
        // render_checkout (skipcash-link) returns an HTML page that itself does
        // window.location.href = payUrl when hit without ?format=json - a plain top-level
        // navigation is all that's needed here, no client-side payment code of our own.
        window.location.href = `${base}/skipcash-link?${params.toString()}`;
    };

    if (loading) return <CircularLoading />;

    return (
        <div className={classes.container} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            <Card className={classes.card}>
                <CardContent style={{ padding: '10px' }}>
                    <Typography className={classes.logo}>RAHL</Typography>
                    <Typography
                        variant="h6"
                        style={{ textAlign: 'center', color: 'var(--text-main)', fontWeight: 'bold', marginBottom: '24px' }}
                    >
                        {t('renew_title')}
                    </Typography>

                    {step === 'lookup' && (
                        <>
                            <TextField
                                label={t('registered_email')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                className={classes.textField}
                                placeholder="you@example.com"
                                type="email"
                            />
                            <Button onClick={handleSendCode} className={classes.btn} variant="contained" fullWidth>
                                {t('renew_send_code')}
                            </Button>
                        </>
                    )}

                    {step === 'otp' && (
                        <>
                            <Typography style={{ color: 'var(--text-secondary)', marginBottom: '16px', textAlign: 'center' }}>
                                {t('renew_otp_sent_to')} {email}
                            </Typography>
                            <TextField
                                label={t('renew_verification_code')}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                fullWidth
                                className={classes.textField}
                                placeholder="123456"
                                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            />
                            <Button onClick={handleVerifyOtp} className={classes.btn} variant="contained" fullWidth>
                                {t('renew_verify_continue')}
                            </Button>
                            <Button onClick={handleSendCode} className={classes.linkBtn} fullWidth>
                                {t('renew_resend_code')}
                            </Button>
                        </>
                    )}

                    {step === 'picker' && (
                        <>
                            <Typography style={{ color: 'var(--text-secondary)', marginBottom: '12px', textAlign: 'center' }}>
                                {t('renew_pick_vehicle')}
                            </Typography>
                            <List>
                                {registrations.map((reg) => {
                                    const expired = reg.expiryDate && reg.expiryDate < Date.now();
                                    return (
                                        <ListItemButton
                                            key={reg.registrationId}
                                            onClick={() => handlePick(reg)}
                                            className={classes.pickerItem}
                                        >
                                            <ListItemText
                                                primary={<Typography style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>{reg.vehicleNumber || reg.serialNumber}</Typography>}
                                                secondary={
                                                    <Chip
                                                        size="small"
                                                        label={expired ? t('renew_status_expired') : t('renew_status_active')}
                                                        style={{
                                                            marginTop: '4px',
                                                            backgroundColor: expired ? 'rgba(239, 68, 68, 0.15)' : 'rgba(233, 174, 49, 0.15)',
                                                            color: expired ? '#ef4444' : '#E9AE31',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </>
                    )}

                    {step === 'status' && selected && (
                        <>
                            <div className={classes.statusRow}>
                                <Typography style={{ color: 'var(--text-secondary)' }}>{t('vehicle_number')}</Typography>
                                <Typography style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>{selected.vehicleNumber || '—'}</Typography>
                            </div>
                            <div className={classes.statusRow} style={{ borderBottom: 'none' }}>
                                <Typography style={{ color: 'var(--text-secondary)' }}>{t('renew_status')}</Typography>
                                <Chip
                                    size="small"
                                    label={isExpired
                                        ? t('renew_status_expired')
                                        : (selected.expiryDate ? `${t('renew_status_active_until')} ${moment(selected.expiryDate).format('LL')}` : t('renew_status_active'))}
                                    style={{
                                        backgroundColor: isExpired ? 'rgba(239, 68, 68, 0.15)' : 'rgba(233, 174, 49, 0.15)',
                                        color: isExpired ? '#ef4444' : '#E9AE31',
                                        fontWeight: 'bold'
                                    }}
                                />
                            </div>

                            <Button
                                onClick={handleRenew}
                                className={classes.btn}
                                variant="contained"
                                fullWidth
                                style={{ marginTop: '20px' }}
                            >
                                {t('renew_pay_button')} {price} QAR
                            </Button>
                            {registrations.length > 1 && (
                                <Button onClick={() => setStep('picker')} className={classes.linkBtn} fullWidth>
                                    {t('renew_choose_different')}
                                </Button>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={alertState.open} onClose={handleAlertClose}>
                {alertState.msg}
            </AlertDialog>
        </div>
    );
}
